import React, { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import { Button, Spinner } from "react-bootstrap";
import Card from "src/components/Cards/Basic";
import FormCheckbox from "src/components/Forms/FormCheckbox";
import {
  showSuccessToast,
  showValidationErrorToast,
} from "src/components/Toasts";
import axios from "src/config/axios";
import { useTranslation } from "react-i18next";
import FormTranslatedEditor from "src/components/Forms/FormTranslatedEditor";
import LanguageSettingsDialog from "./LanguageSettingsDialog";

const TicketVendingMachineSettingsPage = () => {
  const [ticketVendingMachineSetting, setTicketVendingMachineSetting] =
    useState();
  const [showLanguageSettingsDialog, setShowLanguageSettingsDialog] =
    useState(false);
  const [vivaOnboardingLoading, setVivaOnboardingLoading] = useState(false);
  const { t } = useTranslation("dashboard");
  const vivaMerchantId = ticketVendingMachineSetting?.viva_merchant_id;

  useEffect(() => {
    axios
      .get("/api/v1/ticket_vending_machine_setting.json")
      .then(({ data }) => {
        setTicketVendingMachineSetting(data);
      });
  }, []);

  function handleSubmit(values, helpers) {
    helpers.setSubmitting(true);
    axios
      .put("/api/v1/ticket_vending_machine_setting.json", {
        ticket_vending_machine_setting: values,
      })
      .then(({ data }) => {
        setTicketVendingMachineSetting(data);
        showSuccessToast();
      })
      .catch((error) => {
        helpers.setErrors(error.response.data);
        showValidationErrorToast(error);
      })
      .finally(() => {
        helpers.setSubmitting(false);
      });
  }

  function handleVivaOnboarding() {
    const vivaWindow = window.open("", "_blank");
    if (!vivaWindow) {
      alert("Popup blocked");
      return;
    }

    vivaWindow.document.documentElement.innerHTML =
      "<head><title>Viva</title></head><body>Loading Viva...</body>";
    setVivaOnboardingLoading(true);

    axios
      .post("/api/v1/viva_account.json")
      .then(({ data }) => {
        vivaWindow.location.href = data.redirect_url;
      })
      .catch((error) => {
        vivaWindow.close();
        showValidationErrorToast(error);
      })
      .finally(() => {
        setVivaOnboardingLoading(false);
      });
  }

  return (
    <Card title={t("settings.business.ticket_vending_machine.title")}>
      {!ticketVendingMachineSetting ? (
        <Spinner
          className={"d-block mx-auto"}
          animation="border"
          variant="primary"
        />
      ) : (
        <>
          <Button
            className={"mb-3"}
            onClick={() => setShowLanguageSettingsDialog(true)}
            size={"sm"}
            type={"button"}
            variant={"outline-primary"}
          >
            {t(
              "settings.business.ticket_vending_machine.language_settings.button"
            )}
          </Button>
          <Formik
            initialValues={{
              cash_payment_enabled:
                ticketVendingMachineSetting.cash_payment_enabled ?? false,
              pos_payment_enabled:
                ticketVendingMachineSetting.pos_payment_enabled ?? false,
              viva_pos_enabled:
                ticketVendingMachineSetting.viva_pos_enabled ?? false,
              wc_wash_enabled:
                ticketVendingMachineSetting.wc_wash_enabled ?? false,
              hoppers_enabled:
                ticketVendingMachineSetting.hoppers_enabled ?? false,
              privacy_text: ticketVendingMachineSetting.privacy_text ?? "",
              privacy_text_en:
                ticketVendingMachineSetting.privacy_text_en ?? "",
              privacy_text_it:
                ticketVendingMachineSetting.privacy_text_it ?? "",
            }}
            enableReinitialize={true}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, values }) => (
              <Form>
                <FormCheckbox
                  name={"cash_payment_enabled"}
                  label={t(
                    "settings.business.ticket_vending_machine.cash_payment_enabled.label"
                  )}
                  hint={t(
                    "settings.business.ticket_vending_machine.cash_payment_enabled.hint"
                  )}
                />
                <FormCheckbox
                  name={"pos_payment_enabled"}
                  disabled={values.viva_pos_enabled}
                  label={t(
                    "settings.business.ticket_vending_machine.pos_payment_enabled.label"
                  )}
                  hint={t(
                    "settings.business.ticket_vending_machine.pos_payment_enabled.hint"
                  )}
                />
                <FormCheckbox
                  name={"viva_pos_enabled"}
                  disabled={values.pos_payment_enabled}
                  label={t(
                    "settings.business.ticket_vending_machine.viva_pos_enabled.label"
                  )}
                  hint={t(
                    "settings.business.ticket_vending_machine.viva_pos_enabled.hint"
                  )}
                />
                {/*{values.viva_pos_enabled && !vivaMerchantId && (*/}
                {/*  <Button*/}
                {/*    className={"mb-3"}*/}
                {/*    disabled={vivaOnboardingLoading}*/}
                {/*    onClick={handleVivaOnboarding}*/}
                {/*    size={"sm"}*/}
                {/*    type={"button"}*/}
                {/*    variant={"outline-primary"}*/}
                {/*  >*/}
                {/*    {t(*/}
                {/*      "settings.business.ticket_vending_machine.viva_onboarding"*/}
                {/*    )}*/}
                {/*  </Button>*/}
                {/*)}*/}
                <FormCheckbox
                  name={"hoppers_enabled"}
                  label={t(
                    "settings.business.ticket_vending_machine.hoppers_enabled.label"
                  )}
                  hint={t(
                    "settings.business.ticket_vending_machine.hoppers_enabled.hint"
                  )}
                />
                {ticketVendingMachineSetting.configuration.addons
                  .wc_wash_enabled && (
                  <FormCheckbox
                    name={"wc_wash_enabled"}
                    label={t(
                      "settings.business.ticket_vending_machine.wc_wash_enabled.label"
                    )}
                    hint={t(
                      "settings.business.ticket_vending_machine.wc_wash_enabled.hint"
                    )}
                  />
                )}
                <FormTranslatedEditor
                  name="privacy_text"
                  label={t(
                    "settings.business.ticket_vending_machine.privacy_text.label"
                  )}
                  hint={t(
                    "settings.business.ticket_vending_machine.privacy_text.hint"
                  )}
                />

                <Button type={"submit"} disabled={isSubmitting}>
                  {t("common:components.button.save")}
                </Button>
              </Form>
            )}
          </Formik>
          {showLanguageSettingsDialog && (
            <LanguageSettingsDialog
              handleClose={() => setShowLanguageSettingsDialog(false)}
            />
          )}
        </>
      )}
    </Card>
  );
};

export default TicketVendingMachineSettingsPage;
